import type { AttemptEndRequest, AttemptStartRequest, RewardGrantRequest } from '@arrows/shared';
import type { ProgressApi } from './api';

export const outboxStorageKey = 'arrows.outbox.v1';
export const maximumOutboxAttempts = 50;
export const maximumOutboxRewards = 20;
const maximumBackoffMs = 60 * 60 * 1_000;

export interface OutboxAttempt {
  readonly attempt_id: string;
  readonly level: number;
  readonly started_at: string;
  readonly startSent: boolean;
  readonly end?: AttemptEndRequest;
  readonly endSent: boolean;
  readonly reachedGameOver: boolean;
  readonly gameOverSent: boolean;
  readonly retryCount: number;
  readonly retryAfter: number;
}

export interface OutboxDocument {
  readonly generation: number;
  readonly attempts: readonly OutboxAttempt[];
  readonly rewards: readonly OutboxReward[];
}

export interface OutboxReward extends RewardGrantRequest {
  readonly sent: boolean;
  readonly retryCount: number;
  readonly retryAfter: number;
}

type DeliveryResult = 'sent' | 'discard' | 'retry';

function availableLocalStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function isAttempt(value: unknown): value is OutboxAttempt {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<OutboxAttempt>;
  return typeof candidate.attempt_id === 'string'
    && Number.isInteger(candidate.level)
    && typeof candidate.started_at === 'string'
    && typeof candidate.startSent === 'boolean'
    && typeof candidate.endSent === 'boolean'
    && typeof candidate.reachedGameOver === 'boolean'
    && typeof candidate.gameOverSent === 'boolean'
    && Number.isInteger(candidate.retryCount)
    && typeof candidate.retryAfter === 'number';
}

function isReward(value: unknown): value is OutboxReward {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<OutboxReward>;
  return typeof candidate.idempotencyKey === 'string'
    && (candidate.provider === 'mock' || candidate.provider === 'lineyahoo')
    && (candidate.rewardType === 'hint' || candidate.rewardType === 'continue')
    && typeof candidate.grantedAt === 'string'
    && typeof candidate.sent === 'boolean'
    && Number.isInteger(candidate.retryCount)
    && typeof candidate.retryAfter === 'number';
}

function parseDocument(value: unknown): OutboxDocument | null {
  if (typeof value !== 'object' || value === null) return null;
  const candidate = value as Partial<OutboxDocument>;
  if (!Number.isInteger(candidate.generation)
    || Number(candidate.generation) < 0
    || !Array.isArray(candidate.attempts)
    || !candidate.attempts.every(isAttempt)
    || (candidate.rewards !== undefined
      && (!Array.isArray(candidate.rewards) || !candidate.rewards.every(isReward)))) {
    return null;
  }
  return {
    generation: Number(candidate.generation),
    attempts: candidate.attempts,
    rewards: candidate.rewards ?? [],
  };
}

function discardRank(attempt: OutboxAttempt): number {
  if (attempt.end !== undefined && attempt.endSent) return 0;
  if (attempt.end === undefined) return 1;
  return attempt.end.outcome === 'restarted' ? 2 : 3;
}

function enforceLimit(
  attempts: readonly OutboxAttempt[],
  protectedAttemptId?: string,
): OutboxAttempt[] {
  const kept = [...attempts];
  while (kept.length > maximumOutboxAttempts) {
    let discardIndex = kept.findIndex((attempt) => attempt.attempt_id !== protectedAttemptId);
    if (discardIndex < 0) break;
    for (let index = discardIndex + 1; index < kept.length; index += 1) {
      const current = kept[index];
      const selected = kept[discardIndex];
      if (current !== undefined && selected !== undefined
        && current.attempt_id !== protectedAttemptId
        && discardRank(current) < discardRank(selected)) {
        discardIndex = index;
      }
    }
    kept.splice(discardIndex, 1);
  }
  return kept;
}

function enforceRewardLimit(
  rewards: readonly OutboxReward[],
  protectedKey?: string,
): OutboxReward[] {
  const kept = [...rewards];
  while (kept.length > maximumOutboxRewards) {
    const sent = kept.findIndex((reward) => reward.sent && reward.idempotencyKey !== protectedKey);
    const discardIndex = sent >= 0
      ? sent
      : kept.findIndex((reward) => reward.idempotencyKey !== protectedKey);
    if (discardIndex < 0) break;
    kept.splice(discardIndex, 1);
  }
  return kept;
}

export class AttemptOutbox {
  private storageEnabled: boolean;
  private flushing: Promise<void> | null = null;
  private flushRequested = false;

  constructor(
    private readonly api: ProgressApi,
    private readonly storage: Storage | null = availableLocalStorage(),
    private readonly now: () => number = Date.now,
  ) {
    this.storageEnabled = storage !== null;
  }

  get enabled(): boolean {
    return this.storageEnabled;
  }

  snapshot(): OutboxDocument | null {
    return this.read();
  }

  enqueueAttempt(request: AttemptStartRequest): void {
    this.update((document) => ({
      attempts: enforceLimit([
        ...document.attempts.filter((attempt) => attempt.attempt_id !== request.attemptId),
        {
          attempt_id: request.attemptId,
          level: request.level,
          started_at: request.startedAt,
          startSent: false,
          endSent: false,
          reachedGameOver: false,
          gameOverSent: false,
          retryCount: 0,
          retryAfter: 0,
        },
      ], request.attemptId),
      rewards: document.rewards,
    }));
  }

  enqueueReward(request: RewardGrantRequest): void {
    this.update((document) => ({
      attempts: document.attempts,
      rewards: enforceRewardLimit([
        ...document.rewards.filter((reward) => !(
          reward.provider === request.provider
          && reward.idempotencyKey === request.idempotencyKey
        )),
        { ...request, sent: false, retryCount: 0, retryAfter: 0 },
      ], request.idempotencyKey),
    }));
  }

  endAttempt(attemptId: string, end: AttemptEndRequest): void {
    this.replace(attemptId, (attempt) => ({ ...attempt, end, endSent: false }));
  }

  markGameOver(attemptId: string): void {
    this.replace(attemptId, (attempt) => ({ ...attempt, reachedGameOver: true }));
  }

  flush(): Promise<void> {
    if (!this.storageEnabled) return Promise.resolve();
    if (this.flushing !== null) {
      this.flushRequested = true;
      return this.flushing;
    }
    this.flushing = this.flushUntilIdle().finally(() => {
      this.flushing = null;
    });
    return this.flushing;
  }

  private async flushUntilIdle(): Promise<void> {
    do {
      this.flushRequested = false;
      await this.flushPending();
    } while (this.flushRequested);
  }

  private async flushPending(): Promise<void> {
    const attemptIds = this.read()?.attempts.map((attempt) => attempt.attempt_id) ?? [];
    for (const attemptId of attemptIds) {
      let attempt = this.find(attemptId);
      if (attempt === undefined || attempt.retryAfter > this.now()) continue;

      if (!attempt.startSent) {
        const start: AttemptStartRequest = {
          attemptId: attempt.attempt_id,
          level: attempt.level,
          startedAt: attempt.started_at,
        };
        const result = await this.deliver(() => this.api.sendStart(start));
        if (!this.applyDelivery(attemptId, result, (current) => ({
          ...current,
          startSent: true,
          retryCount: 0,
          retryAfter: 0,
        }))) continue;
      }

      attempt = this.find(attemptId);
      if (attempt === undefined) continue;
      if (attempt.reachedGameOver && !attempt.gameOverSent && !attempt.endSent) {
        const result = await this.deliver(() => this.api.sendGameOver(attemptId));
        if (!this.applyDelivery(attemptId, result, (current) => ({
          ...current,
          gameOverSent: true,
          retryCount: 0,
          retryAfter: 0,
        }))) continue;
      }

      attempt = this.find(attemptId);
      if (attempt?.end !== undefined && !attempt.endSent) {
        const result = await this.deliver(() => this.api.sendEnd(attemptId, attempt.end!));
        if (!this.applyDelivery(attemptId, result, (current) => ({
          ...current,
          endSent: true,
          retryCount: 0,
          retryAfter: 0,
        }))) continue;
        this.remove(attemptId);
      }
    }

    const rewardKeys = this.read()?.rewards.map((reward) => (
      `${reward.provider}\0${reward.idempotencyKey}`
    )) ?? [];
    for (const rewardKey of rewardKeys) {
      const reward = this.findReward(rewardKey);
      if (reward === undefined) continue;
      if (reward.sent) {
        this.removeReward(rewardKey);
        continue;
      }
      if (reward.retryAfter > this.now()) continue;
      const result = await this.deliver(() => this.api.sendReward({
        idempotencyKey: reward.idempotencyKey,
        provider: reward.provider,
        rewardType: reward.rewardType,
        grantedAt: reward.grantedAt,
      }));
      if (result === 'sent') {
        this.replaceReward(rewardKey, (current) => ({
          ...current,
          sent: true,
          retryCount: 0,
          retryAfter: 0,
        }));
      } else if (result === 'discard') {
        this.removeReward(rewardKey);
      } else {
        this.replaceReward(rewardKey, (current) => {
          const retryCount = current.retryCount + 1;
          return {
            ...current,
            retryCount,
            retryAfter: this.now() + Math.min(
              maximumBackoffMs,
              1_000 * 2 ** (retryCount - 1),
            ),
          };
        });
      }
    }
  }

  private async deliver(send: () => Promise<Response>): Promise<DeliveryResult> {
    try {
      const response = await send();
      if (response.ok) return 'sent';
      if (response.status === 409 || response.status === 413 || response.status === 422) {
        console.warn(`Discarding progress outbox record after HTTP ${response.status}.`);
        return 'discard';
      }
      return 'retry';
    } catch {
      return 'retry';
    }
  }

  private applyDelivery(
    attemptId: string,
    result: DeliveryResult,
    sent: (attempt: OutboxAttempt) => OutboxAttempt,
  ): boolean {
    if (result === 'discard') {
      this.remove(attemptId);
      return false;
    }
    if (result === 'retry') {
      this.replace(attemptId, (attempt) => {
        const retryCount = attempt.retryCount + 1;
        return {
          ...attempt,
          retryCount,
          retryAfter: this.now() + Math.min(maximumBackoffMs, 1_000 * 2 ** (retryCount - 1)),
        };
      });
      return false;
    }
    this.replace(attemptId, sent);
    return true;
  }

  private find(attemptId: string): OutboxAttempt | undefined {
    return this.read()?.attempts.find((attempt) => attempt.attempt_id === attemptId);
  }

  private findReward(rewardKey: string): OutboxReward | undefined {
    return this.read()?.rewards.find((reward) => (
      `${reward.provider}\0${reward.idempotencyKey}` === rewardKey
    ));
  }

  private replace(
    attemptId: string,
    replacement: (attempt: OutboxAttempt) => OutboxAttempt,
  ): void {
    this.update((document) => ({
      attempts: document.attempts.map((attempt) => (
        attempt.attempt_id === attemptId ? replacement(attempt) : attempt
      )),
      rewards: document.rewards,
    }));
  }

  private remove(attemptId: string): void {
    this.update((document) => ({
      attempts: document.attempts.filter((attempt) => attempt.attempt_id !== attemptId),
      rewards: document.rewards,
    }));
  }

  private replaceReward(
    rewardKey: string,
    replacement: (reward: OutboxReward) => OutboxReward,
  ): void {
    this.update((document) => ({
      attempts: document.attempts,
      rewards: document.rewards.map((reward) => (
        `${reward.provider}\0${reward.idempotencyKey}` === rewardKey
          ? replacement(reward)
          : reward
      )),
    }));
  }

  private removeReward(rewardKey: string): void {
    this.update((document) => ({
      attempts: document.attempts,
      rewards: document.rewards.filter((reward) => (
        `${reward.provider}\0${reward.idempotencyKey}` !== rewardKey
      )),
    }));
  }

  private update(
    mutator: (document: OutboxDocument) => Pick<OutboxDocument, 'attempts' | 'rewards'>,
  ): void {
    for (let retry = 0; retry < 3; retry += 1) {
      const before = this.read();
      if (before === null) return;
      const next = mutator(before);
      const latest = this.read();
      if (latest === null) return;
      if (latest.generation !== before.generation) continue;
      if (this.write({ generation: before.generation + 1, ...next })) return;
    }
  }

  private read(): OutboxDocument | null {
    if (!this.storageEnabled || this.storage === null) return null;
    try {
      const serialized = this.storage.getItem(outboxStorageKey);
      if (serialized === null) return { generation: 0, attempts: [], rewards: [] };
      const parsed: unknown = JSON.parse(serialized);
      const document = parseDocument(parsed);
      if (document !== null) return document;
      this.storage.removeItem(outboxStorageKey);
      return { generation: 0, attempts: [], rewards: [] };
    } catch {
      this.storageEnabled = false;
      return null;
    }
  }

  private write(document: OutboxDocument): boolean {
    if (!this.storageEnabled || this.storage === null) return false;
    try {
      this.storage.setItem(outboxStorageKey, JSON.stringify(document));
      return true;
    } catch {
      this.storageEnabled = false;
      return false;
    }
  }
}
